import { useLocation, useNavigate } from "react-router-dom";
import { useEffect } from "react";
import { useTranslation } from "react-i18next";
import { LanguageSelector } from "@/components/LanguageSelector";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { useAuth } from "@/contexts/AuthContext";
import { AppLayout } from "@/components/layouts/AppLayout";

// The card, brand mark and copy match the rest of the public screens instead
// of a bare HTTP status code, and the exit respects who is looking: a
// customer with no session gets sent to sign in, a signed-in tenant stays
// inside the app shell and lands on their dashboard, never the sales page
// they have no reason to see again (P-07).
function NotFoundCard() {
  const { t } = useTranslation('common');
  const navigate = useNavigate();

  return (
    <Card className="w-full max-w-md">
      <CardHeader className="text-center">
        <CardTitle className="text-2xl font-bold">OKOS</CardTitle>
        <CardDescription className="text-lg text-foreground">{t('pageNotFound')}</CardDescription>
      </CardHeader>
      <CardContent className="space-y-4">
        <p className="text-sm text-muted-foreground text-center">{t('pageNotFoundReason')}</p>
        <Button className="w-full" onClick={() => navigate('/auth')}>
          {t('notFoundSignIn')}
        </Button>
        {/* P-07: a customer who lands here has no account to sign into. The exit
            that always exists is the WhatsApp conversation they came from. */}
        <p className="text-xs text-muted-foreground text-center">
          {t('notFoundBuyerHint')}
        </p>
      </CardContent>
    </Card>
  );
}

const NotFound = () => {
  const location = useLocation();
  const navigate = useNavigate();
  const { t } = useTranslation('common');
  const { user, session, loading } = useAuth();

  useEffect(() => {
    console.error(
      "404 Error: User attempted to access non-existent route:",
      location.pathname
    );
  }, [location.pathname]);

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-background">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary" />
      </div>
    );
  }

  if (user && session) {
    return (
      <AppLayout>
        <div className="flex min-h-[60vh] items-center justify-center p-6">
          <Card className="w-full max-w-md">
            <CardHeader className="text-center">
              <CardTitle className="text-2xl font-bold">{t('pageNotFound')}</CardTitle>
              <CardDescription>{t('pageNotFoundReason')}</CardDescription>
            </CardHeader>
            <CardContent>
              <Button className="w-full" onClick={() => navigate('/dashboard')}>
                {t('returnToDashboard')}
              </Button>
            </CardContent>
          </Card>
        </div>
      </AppLayout>
    );
  }

  return (
    <div className="min-h-screen flex items-center justify-center bg-background p-4">
      <div className="absolute top-4 right-4">
        <LanguageSelector />
      </div>
      <NotFoundCard />
    </div>
  );
};

export default NotFound;
